import { GlobalState } from '@leverege/ui-redux'
import { DateTime } from 'luxon'
import { useEffect, useMemo, useRef, useState } from 'react'
import { useSelector } from 'react-redux'
import { useSharedContext } from '@leverege/ui-plugin'

import { OperatingHours } from '@leverege/operating-hours'
import useStoreTrafficEvents from '../../hooks/useStoreTrafficEvents'

const PER_PAGE = 1000
const RELOAD_TIME = 60000

export default function useHistoricalCountsJanked( opts ) {
  const { delegate, type = 'person' } = opts
  const { operatingHours } = useSharedContext()
  const [ timeKey, setTimeKey ] = useState( Date.now() )
  const timerId = useRef()

  useEffect( () => {
    const load = () => {
      setTimeout( () => {
        setTimeKey( Date.now() )
        load()
      }, RELOAD_TIME )
    }

    load()

    return () => {
      if ( timerId.current ) {
        clearTimeout( timerId.current )
      }
    }
  }, [] )

  const timezone = OperatingHours.getTimezone( operatingHours )

  const queryNames = {
    yesterday : `trafficStats.yesterday.${type}`,
    thisWeek : `trafficStats.thisWeek.${type}`,
    thisMonth : `trafficStats.thisMonth.${type}`
  }

  const ranges = useMemo( () => {
    const now = DateTime
      .now()
      .setZone( timezone )

    return {
      yesterday : {
        start : now.minus( { day : 1 } ).startOf( 'day' ),
        end : now.minus( { day : 1 } ).endOf( 'day' ),
      },
      thisWeek : {
        start : now.startOf( 'week' )
      },
      thisMonth : {
        start : now.startOf( 'month' )
      }
    }
  }, [] )

  const { yesterday, thisWeek, thisMonth } = useSelector( ( state ) => {
    return Object
      .entries( queryNames )
      .reduce( ( prev, [ key, value ] ) => {
        prev[key] = delegate.eventsDelegate.getList( state, value, { getAll : true } )

        return prev
      }, {} )
  } )

  const { result : yesterdayEvents } = useStoreTrafficEvents( { result : yesterday, operatingHours } )
  const { result : thisWeekEvents } = useStoreTrafficEvents( { result : thisWeek, operatingHours } )
  const { result : thisMonthEvents } = useStoreTrafficEvents( { result : thisMonth, operatingHours } )

  const yesterdayCount = useMemo( () => {
    return yesterdayEvents
      .items
      ?.reduce( ( prev, curr ) => {
        if ( curr?.data?.data?.counts[type] != null ) {
          return prev + 1
        }

        return prev
      }, 0 )
  }, [ yesterdayEvents, type ] )

  const thisWeekCount = useMemo( () => {
    return thisWeekEvents
      .items
      ?.reduce( ( prev, curr ) => {
        if ( curr?.data?.data?.counts[type] != null ) {
          return prev + 1
        }

        return prev
      }, 0 )
  }, [ thisWeekEvents, type ] )

  const thisMonthCount = useMemo( () => {
    return thisMonthEvents
      .items
      ?.reduce( ( prev, curr ) => {
        if ( curr?.data?.data?.counts[type] != null ) {
          return prev + 1
        }

        return prev
      }, 0 )
  }, [ thisMonthEvents, type ] )

  useEffect( () => {
    console.log( 'load data' )
    Object
      .entries( queryNames )
      .forEach( async ( [ key, queryName ] ) => {
        let p = 0
        let hasMoreData = true

        while ( hasMoreData ) {
          // eslint-disable-next-line no-await-in-loop
          const { items } = await GlobalState.dispatch(
            delegate.eventsDelegate.search( {
              query : {
                ...ranges[key],
                perPage : PER_PAGE,
                startIndex : p * PER_PAGE,
                where : [
                  [ '$data/analyticsType', '=', 'traffic' ],
                  [ '$data/featureName', '=', 'entry' ],
                  [ `$data/data/counts/${type}`, '>=', 0 ]
                ]
              },
              queryName
            } )
          )

          p += 1

          if ( items.length < PER_PAGE ) {
            hasMoreData = false
          }
        }
        
      } )
  }, [ timeKey ] )

  return {
    yesterday : yesterdayCount || 0,
    thisWeek : thisWeekCount || 0,
    thisMonth : thisMonthCount || 0
  }
}
